import type { FinancialPeriod } from '@/lib/api/financial/financial-dashboard-api'
import {
    getMarketingDashboard,
    syncAdMetrics,
    MarketingComparisonMode,
    MarketingDashboardResponse,
    MarketingDashboardScope,
    MARKETING_ORIGIN_LABELS,
    MarketingOrigin,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { setAccessibleCompanies } from '@/store/slices/authSlice'
import FinancialPeriodSelector from '@/pages/admin/financial/dashboard/components/financial-period-selector'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdsClick,
    MdAttachMoney,
    MdBusiness,
    MdCampaign,
    MdChat,
    MdEmojiEvents,
    MdHelpOutline,
    MdHub,
    MdInfoOutline,
    MdPercent,
    MdPersonAdd,
    MdReceipt,
    MdRefresh,
    MdShoppingCart,
    MdSync,
    MdTrendingUp,
} from 'react-icons/md'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ComparisonSelector from './components/comparison-selector'
import MarketingAttendanceSection from './components/marketing-attendance-section'
import { AdCampaignsTable, OwnCampaignsTable } from './components/marketing-campaign-tables'
import MarketingChannelTable from './components/marketing-channel-table'
import {
    formatCurrency,
    formatCurrencyCompact,
    formatNumber,
    formatPercent,
    formatRange,
} from './components/marketing-format'
import MarketingFunnel from './components/marketing-funnel'
import MarketingHelpModal, { MarketingHelpTopic } from './components/marketing-help-modal'
import MarketingKpiCard from './components/marketing-kpi-card'
import MarketingTrendChart from './components/marketing-trend-chart'

const PERIODS: FinancialPeriod[] = ['today', 'week', 'month', 'last_month', 'quarter', 'year', 'custom']
const ORIGIN_HINTS: Record<MarketingOrigin, string> = {
    paid: 'Só leads e vendas que vieram de anúncio (Meta, Google, TikTok)',
    organic: 'Tudo o que não veio de anúncio',
    all: 'Anúncio e orgânico somados (bate com o dashboard financeiro)',
}
const COMPARE_MODES: MarketingComparisonMode[] = ['previous', 'year', 'custom', 'none']

/**
 * Dashboard de marketing: aquisição (de onde vêm os leads), resultado (quanto vira
 * venda, pela regra do financeiro), atendimento e campanhas — sempre contra um período
 * comparado.
 *
 * Todos os filtros moram na URL: o link compartilhado abre na mesma visão, e voltar de
 * uma campanha não perde o período escolhido.
 */
export default function MarketingDashboardPage() {
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const accessibleCompanies = useAppSelector(s => s.auth.accessibleCompanies)
    const [searchParams, setSearchParams] = useSearchParams()

    const periodParam = searchParams.get('period') as FinancialPeriod | null
    const period: FinancialPeriod = periodParam && PERIODS.includes(periodParam) ? periodParam : 'month'
    const customStart = searchParams.get('start') ?? ''
    const customEnd = searchParams.get('end') ?? ''
    const compareParam = searchParams.get('compare') as MarketingComparisonMode | null
    const compare: MarketingComparisonMode =
        compareParam && COMPARE_MODES.includes(compareParam) ? compareParam : 'previous'
    const compareStart = searchParams.get('cstart') ?? ''
    const compareEnd = searchParams.get('cend') ?? ''
    const scope: MarketingDashboardScope = searchParams.get('scope') === 'company' ? 'company' : 'organization'
    // Padrão: tráfego pago. A tela é para avaliar o marketing; somar o orgânico punha na
    // conta dele toda a venda da clínica.
    const originParam = searchParams.get('origin') as MarketingOrigin | null
    const origin: MarketingOrigin = originParam === 'organic' || originParam === 'all' ? originParam : 'paid'

    const updateParams = (patch: Record<string, string | null>) => {
        const params = new URLSearchParams(searchParams)
        for (const [key, value] of Object.entries(patch)) {
            if (value) params.set(key, value)
            else params.delete(key)
        }
        setSearchParams(params, { replace: true })
    }

    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [data, setData] = useState<MarketingDashboardResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [helpTopic, setHelpTopic] = useState<MarketingHelpTopic | null>(null)
    const hasDataRef = useRef(false)
    const [syncingAds, setSyncingAds] = useState(false)

    const load = useCallback(
        async (isRefresh = false) => {
            if (!user?.companyId) return
            if (period === 'custom' && (!customStart || !customEnd)) {
                setLoading(false)
                return
            }
            if (compare === 'custom' && (!compareStart || !compareEnd)) {
                setLoading(false)
                return
            }

            if (isRefresh || hasDataRef.current) setRefreshing(true)
            else setLoading(true)
            setError(null)

            try {
                const response = await getMarketingDashboard({
                    period,
                    startDate: period === 'custom' ? customStart : undefined,
                    endDate: period === 'custom' ? customEnd : undefined,
                    compare,
                    compareStartDate: compare === 'custom' ? compareStart : undefined,
                    compareEndDate: compare === 'custom' ? compareEnd : undefined,
                    scope,
                    origin,
                })
                if (response.success) {
                    hasDataRef.current = true
                    setData(response.result)
                } else {
                    setError(response.error || 'Erro ao carregar o dashboard de marketing')
                }
            } catch (err) {
                setError('Erro ao carregar o dashboard de marketing')
                console.error(err)
            } finally {
                setLoading(false)
                setRefreshing(false)
            }
        },
        [user?.companyId, period, customStart, customEnd, compare, compareStart, compareEnd, scope, origin],
    )

    useEffect(() => {
        if (user?.companyId) load()
    }, [user?.companyId, load])

    const runAdSync = async () => {
        setSyncingAds(true)
        try {
            const response = await syncAdMetrics()
            if (!response.success) {
                toast.error(response.error || 'Não foi possível ler o desempenho dos anúncios')
                return
            }
            const { accounts, rows, errors } = response.result
            if (accounts === 0) {
                toast.info('Nenhuma conta de anúncio conectada por login na Meta para ler o desempenho.')
            } else if (errors.length) {
                toast.error(`Falha ao ler ${errors.length} conta(s): ${errors[0].message}`)
            } else {
                toast.success(`Desempenho dos anúncios atualizado (${formatNumber(rows)} linha(s)).`)
            }
            await load(true)
        } catch {
            toast.error('Não foi possível ler o desempenho dos anúncios')
        } finally {
            setSyncingAds(false)
        }
    }

    useEffect(() => {
        if (accessibleCompanies.length === 0) {
            getAccessibleCompanies()
                .then(res => {
                    if (res.success && res.result) dispatch(setAccessibleCompanies(res.result))
                })
                .catch(() => {})
        }
    }, [accessibleCompanies.length, dispatch])

    const filters = (
        <div className="flex flex-col gap-3">
            <FinancialPeriodSelector
                value={period}
                onChange={next => updateParams({ period: next === 'month' ? null : next })}
                startDate={customStart}
                endDate={customEnd}
                onCustomDatesChange={(start, end) => updateParams({ start, end })}
            />
            <ComparisonSelector
                value={compare}
                onChange={next => updateParams({ compare: next === 'previous' ? null : next })}
                startDate={compareStart}
                endDate={compareEnd}
                onCustomDatesChange={(start, end) => updateParams({ cstart: start, cend: end })}
                resolved={data?.compareRange ?? null}
            />
        </div>
    )

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="loading loading-spinner loading-lg text-secondary" />
                <p className="text-base-content/60 text-sm">Carregando dashboard de marketing...</p>
            </div>
        )
    }

    const header = (
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
                <h1 className="text-2xl font-bold text-base-content">Dashboard de Marketing</h1>
                {data && (
                    <p className="text-sm text-base-content/60">
                        {formatRange(data.range)}
                        {data.compareRange && <> · comparado com {formatRange(data.compareRange)}</>}
                        {' · '}Atualizado às{' '}
                        {new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
                            new Date(data.lastUpdated),
                        )}
                    </p>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <div className="join" role="group" aria-label="Origem dos leads e das vendas">
                    {(['paid', 'organic', 'all'] as MarketingOrigin[]).map(option => (
                        <button
                            key={option}
                            type="button"
                            className={`btn btn-sm join-item ${origin === option ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => updateParams({ origin: option === 'paid' ? null : option })}
                            title={ORIGIN_HINTS[option]}
                        >
                            {MARKETING_ORIGIN_LABELS[option]}
                        </button>
                    ))}
                </div>
                {accessibleCompanies.length > 1 && (
                    <div className="join">
                        <button
                            type="button"
                            className={`btn btn-sm join-item gap-1 ${scope === 'company' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => updateParams({ scope: 'company' })}
                        >
                            <MdBusiness className="text-base" />
                            Filial atual
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm join-item gap-1 ${scope === 'organization' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => updateParams({ scope: null })}
                        >
                            <MdHub className="text-base" />
                            Todas as filiais
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setHelpTopic('comparison')}
                    className="btn btn-sm btn-ghost border border-base-300 gap-1"
                >
                    <MdHelpOutline className="text-base" />
                    Como ler
                </button>
                <button
                    onClick={() => load(true)}
                    className="btn btn-sm btn-ghost border border-base-300"
                    disabled={refreshing}
                >
                    <MdRefresh className={`text-base ${refreshing ? 'animate-spin' : ''}`} />
                    Atualizar
                </button>
            </div>
        </div>
    )

    if (error || !data) {
        return (
            <div className="flex flex-col gap-6">
                {header}
                {filters}
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                    {error ? (
                        <>
                            <div className="text-error text-lg">Erro ao carregar dashboard</div>
                            <p className="text-base-content/60 text-sm">{error}</p>
                            <button className="btn btn-secondary btn-sm" onClick={() => load(true)}>
                                Tentar novamente
                            </button>
                        </>
                    ) : (
                        <p className="text-base-content/60 text-sm">
                            Escolha as datas e clique em Aplicar para carregar o dashboard.
                        </p>
                    )}
                </div>
            </div>
        )
    }

    const cur = data.current
    const prev = data.previous
    const s = cur.summary
    const p = prev?.summary
    const prevOf = <K extends keyof typeof s>(key: K) => (prev ? (p![key] as number | null) : undefined)
    const adUnavailable =
        s.adSpend != null
            ? undefined
            : cur.adAccounts.length === 0
              ? 'Conecte a conta de anúncio da Meta em Marketing > Meta'
              : 'Sem investimento lido no período'

    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-500">
            {header}
            {filters}

            {data.scope === 'company' && (
                <div className="alert alert-info py-2 text-sm">
                    <MdInfoOutline className="text-lg" />
                    <span>
                        Filial atual: leads e oportunidades que ainda estão na triagem (sem filial) ficam de fora.
                        Campanhas e anúncios são da organização inteira.
                    </span>
                </div>
            )}
            {cur.truncated && (
                <div className="alert alert-warning py-2 text-sm">
                    <MdInfoOutline className="text-lg" />
                    <span>O período tem mais de 10 mil vendas de um tipo: os totais de venda podem estar abaixo do real.</span>
                </div>
            )}

            <section className="flex flex-col gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50">Aquisição</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                    <MarketingKpiCard
                        title="Leads"
                        value={s.leads}
                        previous={prevOf('leads')}
                        format={formatNumber}
                        icon={MdPersonAdd}
                        color="primary"
                        subtitle={
                            s.leadsCreatedAtSale
                                ? `+${formatNumber(s.leadsCreatedAtSale)} criados no agendamento (fora da conta)`
                                : undefined
                        }
                        onHelp={() => setHelpTopic('leads')}
                    />
                    {origin === 'all' && (
                        <MarketingKpiCard
                            title="Leads de anúncio"
                            value={s.paidLeads}
                            previous={prevOf('paidLeads')}
                            format={formatNumber}
                            icon={MdAdsClick}
                            color="info"
                            subtitle={s.leads ? `${formatPercent((s.paidLeads / s.leads) * 100)} dos leads` : undefined}
                            onHelp={() => setHelpTopic('leads')}
                        />
                    )}
                    {origin === 'paid' && (
                        <MarketingKpiCard
                            title="Conversas na Meta"
                            value={s.platformResults}
                            previous={prev ? p!.platformResults : undefined}
                            format={formatNumber}
                            icon={MdAdsClick}
                            color="info"
                            unavailable={s.platformResults == null ? 'Sem conta de anúncio sincronizada' : undefined}
                            subtitle={
                                s.platformResults
                                    ? `${formatPercent((s.leads / s.platformResults) * 100)} viraram lead aqui`
                                    : undefined
                            }
                            onHelp={() => setHelpTopic('ads')}
                        />
                    )}
                    <MarketingKpiCard
                        title="Conversas novas"
                        value={s.newChats}
                        previous={prevOf('newChats')}
                        format={formatNumber}
                        icon={MdChat}
                        color="success"
                        onHelp={() => setHelpTopic('attendance')}
                    />
                    <MarketingKpiCard
                        title="Com oportunidade"
                        value={s.leadsWithDeal}
                        previous={prevOf('leadsWithDeal')}
                        format={formatNumber}
                        icon={MdTrendingUp}
                        color="secondary"
                        subtitle="Leads com oportunidade aberta no período"
                        onHelp={() => setHelpTopic('funnel')}
                    />
                    <MarketingKpiCard
                        title="Oportunidades ganhas"
                        value={s.wonLeads}
                        previous={prevOf('wonLeads')}
                        format={formatNumber}
                        icon={MdEmojiEvents}
                        color="warning"
                        subtitle={`Valor no funil: ${formatCurrencyCompact(s.wonValue)}`}
                        onHelp={() => setHelpTopic('won')}
                    />
                    <MarketingKpiCard
                        title="Cliques em campanhas"
                        value={s.campaignClicks}
                        previous={prevOf('campaignClicks')}
                        format={formatNumber}
                        icon={MdCampaign}
                        color="info"
                        subtitle="Links rastreados próprios"
                    />
                </div>
            </section>

            <section className="flex flex-col gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50">Resultado</h2>
                <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${origin === 'paid' ? 'xl:grid-cols-4' : origin === 'organic' ? 'xl:grid-cols-5' : 'xl:grid-cols-6'}`}>
                    <MarketingKpiCard
                        title={origin === 'paid' ? 'Vendas do tráfego pago' : origin === 'organic' ? 'Vendas orgânicas' : 'Vendas'}
                        value={s.salesValue}
                        previous={prevOf('salesValue')}
                        format={formatCurrencyCompact}
                        icon={MdAttachMoney}
                        color="success"
                        subtitle={`${formatNumber(s.salesCount)} ${s.salesCount === 1 ? 'atendimento vendido' : 'atendimentos vendidos'}`}
                        onHelp={() => setHelpTopic('sales')}
                    />
                    {origin === 'all' && (
                        <MarketingKpiCard
                            title="Receita de anúncios"
                            value={s.paidSalesValue}
                            previous={prevOf('paidSalesValue')}
                            format={formatCurrencyCompact}
                            icon={MdAdsClick}
                            color="info"
                            subtitle={
                                s.salesValue
                                    ? `${formatPercent((s.paidSalesValue / s.salesValue) * 100)} das vendas`
                                    : undefined
                            }
                            onHelp={() => setHelpTopic('sales')}
                        />
                    )}
                    <MarketingKpiCard
                        title="Ticket médio"
                        value={s.averageTicket}
                        previous={prevOf('averageTicket')}
                        format={formatCurrency}
                        icon={MdReceipt}
                        color="primary"
                        onHelp={() => setHelpTopic('sales')}
                    />
                    <MarketingKpiCard
                        title="Clientes que compraram"
                        value={s.buyers}
                        previous={prevOf('buyers')}
                        format={formatNumber}
                        icon={MdShoppingCart}
                        color="secondary"
                        onHelp={() => setHelpTopic('sales')}
                    />
                    <MarketingKpiCard
                        title="Lead que virou venda"
                        value={s.leadToSaleRate}
                        previous={prevOf('leadToSaleRate')}
                        format={v => formatPercent(v)}
                        icon={MdPercent}
                        color="warning"
                        subtitle="Leads do período que já compraram"
                        onHelp={() => setHelpTopic('funnel')}
                    />
                    {origin !== 'paid' && (
                        <MarketingKpiCard
                            title="Vendas sem origem"
                            value={s.salesValue - s.attributedSalesValue}
                            previous={prev ? p!.salesValue - p!.attributedSalesValue : undefined}
                            format={formatCurrencyCompact}
                            icon={MdInfoOutline}
                            color="error"
                            polarity="lower-better"
                            subtitle="Cliente que nunca foi lead"
                            onHelp={() => setHelpTopic('channel')}
                        />
                    )}
                </div>
            </section>

            {origin !== 'organic' && (
            <section className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50">
                        Investimento em anúncios
                    </h2>
                    <div className="flex flex-wrap items-center gap-3">
                        {cur.adAccounts.map(account => (
                            <span
                                key={`${account.platform}:${account.accountId}`}
                                className={`text-xs ${account.lastError ? 'text-error' : 'text-base-content/60'}`}
                                title={account.lastError ?? undefined}
                            >
                                {account.platform === 'META' ? 'Meta' : account.platform === 'GOOGLE' ? 'Google' : account.platform}{' '}
                                {account.accountId}:{' '}
                                {account.lastError
                                    ? 'falhou na última leitura'
                                    : account.lastSuccessAt
                                      ? `atualizado em ${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(account.lastSuccessAt))}`
                                      : 'aguardando a primeira leitura'}
                            </span>
                        ))}
                        <button
                            type="button"
                            className="btn btn-xs btn-ghost border border-base-300 gap-1"
                            onClick={runAdSync}
                            disabled={syncingAds}
                        >
                            <MdSync className={`text-sm ${syncingAds ? 'animate-spin' : ''}`} />
                            Sincronizar agora
                        </button>
                    </div>
                </div>
                {cur.adCurrencies.some(c => c !== 'BRL') && (
                    <div className="alert alert-warning py-2 text-sm">
                        <MdInfoOutline className="text-lg" />
                        <span>
                            Há conta de anúncio em {cur.adCurrencies.filter(c => c !== 'BRL').join(', ')}: o investimento
                            é somado sem conversão de moeda.
                        </span>
                    </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MarketingKpiCard
                        title="Investimento"
                        value={s.adSpend}
                        previous={prev ? p!.adSpend : undefined}
                        format={formatCurrencyCompact}
                        icon={MdAttachMoney}
                        color="secondary"
                        polarity="neutral"
                        unavailable={adUnavailable}
                        subtitle={
                            s.adImpressions != null
                                ? `${formatNumber(s.adImpressions)} impressões · ${formatNumber(s.adClicks ?? 0)} cliques`
                                : undefined
                        }
                        onHelp={() => setHelpTopic('investment')}
                    />
                    <MarketingKpiCard
                        title="Custo por lead (CPL)"
                        value={s.costPerLead}
                        previous={prev ? p!.costPerLead : undefined}
                        format={formatCurrency}
                        icon={MdPersonAdd}
                        color="info"
                        polarity="lower-better"
                        unavailable={adUnavailable}
                        subtitle={`${formatNumber(s.paidLeads)} leads de anúncio no sistema`}
                        onHelp={() => setHelpTopic('investment')}
                    />
                    <MarketingKpiCard
                        title="Custo por cliente (CAC)"
                        value={s.costPerBuyer}
                        previous={prev ? p!.costPerBuyer : undefined}
                        format={formatCurrency}
                        icon={MdShoppingCart}
                        color="warning"
                        polarity="lower-better"
                        unavailable={adUnavailable}
                        subtitle={`${formatNumber(s.paidBuyers)} clientes de anúncio compraram`}
                        onHelp={() => setHelpTopic('investment')}
                    />
                    <MarketingKpiCard
                        title="Retorno (ROAS)"
                        value={s.roas}
                        previous={prev ? p!.roas : undefined}
                        format={v => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}x`}
                        icon={MdTrendingUp}
                        color="success"
                        unavailable={adUnavailable}
                        subtitle="Receita de anúncios / investimento"
                        onHelp={() => setHelpTopic('investment')}
                    />
                </div>
            </section>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 min-w-0">
                    <MarketingTrendChart
                        current={cur.series}
                        previous={prev?.series ?? null}
                        granularity={data.granularity}
                        showAdMetrics={origin !== 'organic'}
                    />
                </div>
                <MarketingFunnel
                    current={cur.funnel}
                    previous={prev?.funnel ?? null}
                    onHelp={() => setHelpTopic('funnel')}
                />
            </div>

            <MarketingChannelTable
                current={cur.channels}
                previous={prev?.channels ?? null}
                onHelp={() => setHelpTopic('channel')}
            />

            <MarketingAttendanceSection
                current={cur}
                previous={prev}
                onHelp={() => setHelpTopic('attendance')}
            />

            {origin !== 'organic' && (
            <div className="flex flex-col gap-6">
                <AdCampaignsTable
                    title="Meta Ads"
                    subtitle="Facebook e Instagram: o que a Meta conta e o que chegou ao sistema"
                    platform="META"
                    current={cur.metaAds}
                    previous={prev?.metaAds ?? null}
                    synced={cur.adAccounts.some(a => a.platform === 'META')}
                    onHelp={() => setHelpTopic('ads')}
                />
                <AdCampaignsTable
                    title="Google Ads"
                    subtitle="Leads com clique do Google (gclid) que chegaram ao sistema. O investimento chega com a leitura do Google Ads."
                    platform="GOOGLE"
                    current={cur.googleAds}
                    previous={prev?.googleAds ?? null}
                    synced={cur.adAccounts.some(a => a.platform === 'GOOGLE')}
                    onHelp={() => setHelpTopic('ads')}
                />
            </div>
            )}

            <OwnCampaignsTable current={cur.campaigns} previous={prev?.campaigns ?? null} />

            <MarketingHelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />
        </div>
    )
}
